import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, FileText, TrendingUp, TrendingDown, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const InvoicesPage = () => {
  const { isAdmin } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [clientSearch, setClientSearch] = useState('');

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedMonth) {
        params.append('month', selectedMonth);
      }
      if (clientSearch) {
        params.append('search', clientSearch);
      }
      const queryString = params.toString();
      const data = await api.getInvoices(queryString ? `?${queryString}` : '');
      setInvoices(data);
    } catch (error) {
      toast.error('Erro ao carregar faturas');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, clientSearch]);

  const loadDashboard = useCallback(async () => {
    try {
      const data = await api.getInvoicesDashboard(selectedMonth);
      setDashboardData(data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    }
  }, [selectedMonth]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handlePay = async (id) => {
    if (!window.confirm('Marcar esta fatura como paga?')) return;
    try {
      await api.payInvoice(id);
      toast.success('Fatura marcada como paga');
      loadInvoices();
    } catch (error) {
      toast.error('Erro ao pagar fatura');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar esta fatura?')) return;
    try {
      await api.cancelInvoice(id);
      toast.success('Fatura cancelada');
      loadInvoices();
    } catch (error) {
      toast.error('Erro ao cancelar fatura');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDING: { variant: 'secondary', label: 'Pendente' },
      PAID: { variant: 'default', label: 'Paga' },
      CANCELLED: { variant: 'destructive', label: 'Cancelada' },
    };
    const config = variants[status] || variants.PENDING;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-3xl font-bold">Faturamento</h1>
        <p className="text-muted-foreground mt-1">Gerencie faturas e pagamentos</p>
      </div>

      {/* Dashboard Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Dashboard</h2>
          <div className="flex items-center gap-2">
            <Label htmlFor="month-filter" className="text-sm">Filtrar por mês:</Label>
            <Input
              id="month-filter"
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-40"
            />
          </div>
        </div>

        {dashboardData && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">PDV</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(dashboardData.pdv)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.pdvSales} transação(ões)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Faturas</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(dashboardData.faturas)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.paidInvoices} fatura(s) paga(s)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Saída</CardTitle>
                <TrendingDown className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(dashboardData.saida)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Despesas
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pendente</CardTitle>
                <Clock className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {formatCurrency(dashboardData.pendente)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.pendingInvoices} fatura(s) pendente(s)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Atraso</CardTitle>
                <AlertCircle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(dashboardData.atraso)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.overdueInvoices} fatura(s) vencida(s)
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Invoices List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Faturas</h2>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Pesquisar cliente (nome ou documento)..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="w-64"
            />
          </div>
        </div>
        <div className="grid gap-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id} data-testid={`invoice-card-${invoice.id}`}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-xl">{invoice.description}</CardTitle>
                    {getStatusBadge(invoice.status)}
                  </div>
                  {invoice.customer && (
                    <CardDescription>
                      Cliente: {invoice.customer.name} - {invoice.customer.document}
                    </CardDescription>
                  )}
                  {!invoice.customer && invoice.user && (
                    <CardDescription>Cliente: {invoice.user.name}</CardDescription>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    R$ {invoice.amount.toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Vencimento: {formatDate(invoice.dueDate)}
                  </div>
                </div>
              </CardHeader>
              {isAdmin() && invoice.status === 'PENDING' && (
                <CardContent className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handlePay(invoice.id)}
                    data-testid={`pay-invoice-${invoice.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marcar como Paga
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCancel(invoice.id)}
                    data-testid={`cancel-invoice-${invoice.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {invoices.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">Nenhuma fatura encontrada.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
